import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const LS_KEY = "dep_owner_key";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem(LS_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const tryAuth = useCallback(async (k) => {
    const res = await api.verify(k);
    if (res.ok) {
      setKey(k);
      setAuthed(true);
      sessionStorage.setItem(LS_KEY, k);
      setAuthError("");
    } else {
      setAuthed(false);
      setAuthError("No tienes acceso a esta zona.");
      sessionStorage.removeItem(LS_KEY);
    }
  }, []);

  useEffect(() => {
    if (key && !authed) tryAuth(key);
  }, [key, authed, tryAuth]);

  const load = useCallback(async (search) => {
    setLoading(true);
    const res = await api.listarLeads(key, search);
    setLoading(false);
    if (res.ok) setLeads(res.data.leads);
  }, [key]);

  useEffect(() => {
    if (authed) load("");
  }, [authed, load]);

  const openDetail = async (id) => {
    const res = await api.verLead(key, id);
    if (res.ok) setDetail(res.data);
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--surface)] p-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            tryAuth(input);
          }}
          className="soft-card w-full max-w-md p-8"
          data-testid="admin-login"
        >
          <Link to="/" className="flex items-center gap-2.5">
            <span className="paso-dot" />
            <span className="font-display text-lg text-[var(--text)]" style={{ fontWeight: 600 }}>
              Da el Paso
            </span>
          </Link>
          <h1 className="font-display mt-6 text-2xl text-[var(--text)]" style={{ fontWeight: 600 }}>
            Panel privado
          </h1>
          <p className="mt-2 text-base text-[var(--muted)]">Introduce tu clave de acceso.</p>
          <Input
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            data-testid="admin-key-input"
            className="mt-5 rounded-xl border-[var(--line)] text-base focus-visible:ring-[var(--alba)]"
            placeholder="Clave"
          />
          {authError && <p className="field-error mt-2" data-testid="admin-auth-error">⚠ {authError}</p>}
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="mt-5 w-full rounded-full bg-[var(--cta-bg)] py-3.5 text-base font-semibold text-[var(--cta-fg)]"
          >
            Entrar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="border-b border-[var(--line)] bg-[var(--bg)]">
        <div className="wrap flex h-16 items-center justify-between">
          <span className="font-display text-lg text-[var(--text)]" style={{ fontWeight: 600 }}>
            Panel · Da el Paso
          </span>
          <span className="text-sm text-[var(--muted)]">{leads.length} registros</span>
        </div>
      </header>

      <main className="wrap py-10">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(q);
          }}
          className="relative max-w-md"
        >
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-search"
            placeholder="Buscar por nombre, email o cambio…"
            className="rounded-full border-[var(--line)] pl-11 text-base focus-visible:ring-[var(--alba)]"
          />
        </form>

        {loading ? (
          <p className="mt-8 text-[var(--muted)]">Cargando…</p>
        ) : leads.length === 0 ? (
          <p className="mt-10 text-lg text-[var(--muted)]" data-testid="admin-empty">
            Todavía no hay registros.
          </p>
        ) : (
          <div className="mt-8 overflow-hidden rounded-2xl border border-[var(--line)]" data-testid="admin-table">
            <table className="w-full text-left">
              <thead className="bg-[var(--surface)] text-sm text-[var(--muted)]">
                <tr>
                  <th className="px-5 py-3 font-semibold">Nombre</th>
                  <th className="hidden px-5 py-3 font-semibold sm:table-cell">Email</th>
                  <th className="hidden px-5 py-3 font-semibold md:table-cell">Primer paso</th>
                  <th className="px-5 py-3 font-semibold">Entrada</th>
                  <th className="px-5 py-3 font-semibold">Sesión</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => openDetail(l.id)}
                    data-testid={`admin-row-${l.id}`}
                    className="cursor-pointer border-t border-[var(--line)] text-base hover:bg-[var(--surface)]"
                  >
                    <td className="px-5 py-4 font-semibold text-[var(--text)]">{l.nombre}</td>
                    <td className="hidden px-5 py-4 text-[var(--muted)] sm:table-cell">{l.email}</td>
                    <td className="hidden max-w-xs truncate px-5 py-4 text-[var(--muted)] md:table-cell">{l.primer_paso}</td>
                    <td className="px-5 py-4 text-sm text-[var(--muted)]">{fmtDate(l.fecha_creacion)}</td>
                    <td className="px-5 py-4 text-sm">
                      {l.fecha_sesion ? (
                        <span className="rounded-full bg-[var(--line)] px-2.5 py-1 text-[var(--azul-text)]">Solicitada</span>
                      ) : (
                        <span className="text-[var(--faint)]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Detalle */}
      {detail && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/30"
          onClick={() => setDetail(null)}
        >
          <div
            className="h-full w-full max-w-md overflow-y-auto bg-[var(--bg)] p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-detail"
          >
            <button
              type="button"
              onClick={() => setDetail(null)}
              className="mb-6 text-base text-[var(--muted)] hover:text-[#275FBE]"
            >
              ✕ Cerrar
            </button>
            <h2 className="font-display text-2xl text-[var(--text)]" style={{ fontWeight: 600 }}>
              {detail.nombre}
            </h2>
            <p className="mt-1 text-base text-[var(--muted)]">{detail.email}</p>
            <dl className="mt-6 space-y-5">
              {[
                ["Cambio", detail.cambio],
                ["Importancia", detail.importancia],
                ["Primer paso", detail.primer_paso],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-sm font-semibold uppercase tracking-wide text-[var(--azul-text)]">{k}</dt>
                  <dd className="mt-1 whitespace-pre-wrap text-base text-[var(--muted)]">{v}</dd>
                </div>
              ))}
              <div>
                <dt className="text-sm font-semibold uppercase tracking-wide text-[var(--azul-text)]">Entrada</dt>
                <dd className="mt-1 text-base text-[var(--muted)]">{fmtDate(detail.fecha_creacion)}</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold uppercase tracking-wide text-[var(--azul-text)]">Solicitudes de sesión</dt>
                <dd className="mt-1 text-base text-[var(--muted)]">
                  {detail.solicitudes?.length
                    ? detail.solicitudes.map((s) => (
                        <div key={s.id}>{fmtDate(s.fecha_sesion)} · {s.estado}</div>
                      ))
                    : "Sin solicitud"}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
